import React, { useCallback, useMemo, useRef, useEffect } from "react";
import {
  Divider,
  Flex,
  HStack,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  Text,
  Box,
  Button,
  Input,
  Spacer,
  VStack,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogBody,
  PopoverArrow,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import { FaChevronDown, FaPlus } from "react-icons/fa";
import { useDashboardSelectorController } from "./useDashboardSelectorController";
import { EditIcon, DeleteIcon } from "@chakra-ui/icons";
import { Show } from "../../components";
import { _deleteLayout } from "../../api";
import { DashboardConfiguration } from "../../types/DashboardConfiguration";
import PopoverContentTransparent from "../../components/design_library/PopoverContentTransparent";
import DropDownButton from "../../components/design_library/DropDownButton";
import {
  IoCheckmark,
  IoRadioButtonOff,
  IoRadioButtonOn,
  IoCopyOutline,
} from "react-icons/io5";
import ActionButton from "../../components/design_library/ActionButton";
import { SecondaryButton } from "../../components/design_library";
import { track, EVENTS } from "../../utils/analytics";

export const DashboardSelector = () => {
  const renameDashboardInput = useRef<HTMLInputElement>(null);
  const newDashboardInput = useRef<HTMLInputElement>(null);
  const [showAlert, setShowAlert] = React.useState(false);
  const cancelRef = React.useRef<any>();
  const toast = useToast();

  const [editRow, setEditRow] = React.useState<number | undefined>(undefined);
  const [deleteDashboard, setDeleteDashboardRow] = React.useState<
    DashboardConfiguration | undefined
  >(undefined);
  const [newDashboardName, setNewDashboardName] = React.useState("");
  const [renameValue, setRenameValue] = React.useState("");
  const [activeIndex, setActiveIndex] = React.useState<number>(-1);
  const [expanded, setExpanded] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");

  const controller = useDashboardSelectorController();

  const dashboards = useMemo(() => {
    return controller.configurations.map((item) => {
      return { value: item, label: item.name };
    });
  }, [controller.configurations]);

  const filteredDashboards = useMemo(() => {
    if (!searchQuery.trim()) {
      return dashboards;
    }
    return dashboards.filter(dashboard =>
      dashboard.label.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [dashboards, searchQuery]);

  // Validation helpers
  const validateDashboardName = useCallback((name: string, excludeId?: number) => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      return "Dashboard name cannot be empty";
    }
    
    const existingNames = controller.configurations
      .filter(config => config.configurationId !== excludeId)
      .map(config => config.name.toLowerCase());
    
    if (existingNames.includes(trimmedName.toLowerCase())) {
      return "A dashboard with this name already exists";
    }
    
    return null;
  }, [controller.configurations]);

  const isNewDashboardValid = useMemo(() => {
    return validateDashboardName(newDashboardName) === null;
  }, [newDashboardName, validateDashboardName]);

  const isRenameValid = useMemo(() => {
    return editRow ? validateDashboardName(renameValue, editRow) === null : true;
  }, [renameValue, editRow, validateDashboardName]);

  const doDelete = async () => {
    if (!deleteDashboard?.configurationId) return;

    try {
      track(EVENTS.DASHBOARD.DASHBOARD_DELETED, {
        dashboardId: deleteDashboard.configurationId,
        dashboardName: deleteDashboard.name,
      });
      
      await controller.deleteDashboard(deleteDashboard.configurationId);
      
      toast({
        title: "Dashboard deleted",
        description: `"${deleteDashboard.name}" has been deleted successfully.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete dashboard';
      toast({
        title: "Failed to delete dashboard",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeleteDashboardRow(undefined);
      setShowAlert(false);
    }
  };

  const confirmDelete = useCallback(() => {
    return (
      <AlertDialog
        isOpen={showAlert}
        leastDestructiveRef={cancelRef}
        onClose={() => setShowAlert(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="var(--bg)">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Dashboard ({deleteDashboard?.name})
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure? You can't undo this action afterwards.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setShowAlert(false)}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={() => doDelete()} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    );
  }, [deleteDashboard, showAlert]);

  // Create dashboard handler
  const handleCreateDashboard = async () => {
    if (!isNewDashboardValid || controller.loading.creating) return;

    try {
      const result = await controller.createDashboard(newDashboardName);
      
      if (result) {
        toast({
          title: "Dashboard created",
          description: `"${result.name}" has been created successfully.`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        
        setNewDashboardName("");
        setExpanded(false);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create dashboard';
      toast({
        title: "Failed to create dashboard",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Rename dashboard handler
  const handleRenameDashboard = async (dashboardId: number, newName: string) => {
    if (!isRenameValid || controller.loading.renaming) return;

    try {
      await controller.renameDashboard(dashboardId, newName);
      
      toast({
        title: "Dashboard renamed",
        description: `Dashboard has been renamed to "${newName}".`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      
      setEditRow(undefined);
      setRenameValue("");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to rename dashboard';
      toast({
        title: "Failed to rename dashboard",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle dashboard selection
  const handleSelectDashboard = (dashboard: any) => {
    track(EVENTS.DASHBOARD.DASHBOARD_CHANGED, {
      dashboardId: dashboard.value.configurationId,
      dashboardName: dashboard.label,
    });
    controller.setSelectedConfiguration(dashboard.value.configurationId!);
    setExpanded(false); // Close on select
  };

  // Handle edit mode
  const handleStartEdit = (dashboard: any) => {
    setEditRow(dashboard.value.configurationId);
    setRenameValue(dashboard.label);
    // Focus input after render
    setTimeout(() => {
      renameDashboardInput.current?.focus();
    }, 0);
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditRow(undefined);
    setRenameValue("");
  };

  // Duplicate dashboard handler
  const handleDuplicateDashboard = async (dashboard: any) => {
    const baseName = dashboard.label;
    let duplicateName = `${baseName} (Copy)`;
    let counter = 1;
    
    // Find a unique name
    while (dashboards.some(d => d.label.toLowerCase() === duplicateName.toLowerCase())) {
      counter++;
      duplicateName = `${baseName} (Copy ${counter})`;
    }

    try {
      track('dashboard_duplicated', {
        originalDashboardId: dashboard.value.configurationId,
        originalDashboardName: baseName,
        newDashboardName: duplicateName,
      });
      
      const result = await controller.createDashboard(duplicateName);
      
      if (result) {
        toast({
          title: "Dashboard duplicated",
          description: `"${result.name}" has been created as a copy of "${baseName}".`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to duplicate dashboard';
      
      track('dashboard_duplicate_failed', {
        originalDashboardId: dashboard.value.configurationId,
        originalDashboardName: baseName,
        error: errorMessage,
      });
      
      toast({
        title: "Failed to duplicate dashboard",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Keyboard navigation handler
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!expanded) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex(prev => Math.min(prev + 1, filteredDashboards.length - 1));
        track('dashboard_keyboard_navigation', { action: 'arrow_down' });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex(prev => Math.max(prev - 1, -1));
        track('dashboard_keyboard_navigation', { action: 'arrow_up' });
        break;
      case 'Enter':
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < filteredDashboards.length) {
          track('dashboard_keyboard_navigation', { action: 'enter_select' });
          handleSelectDashboard(filteredDashboards[activeIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        track('dashboard_keyboard_navigation', { action: 'escape_close' });
        setExpanded(false);
        setActiveIndex(-1);
        break;
      default:
        // Typeahead search (only when not in search input)
        if (e.key.length === 1 && document.activeElement?.id !== 'dashboard-search-input') {
          const searchChar = e.key.toLowerCase();
          const currentIndex = activeIndex >= 0 ? activeIndex : -1;
          const matchIndex = filteredDashboards.findIndex((dashboard, index) => 
            index > currentIndex && dashboard.label.toLowerCase().startsWith(searchChar)
          );
          if (matchIndex >= 0) {
            setActiveIndex(matchIndex);
          } else {
            // Wrap around search
            const wrapMatchIndex = filteredDashboards.findIndex(dashboard => 
              dashboard.label.toLowerCase().startsWith(searchChar)
            );
            if (wrapMatchIndex >= 0) {
              setActiveIndex(wrapMatchIndex);
            }
          }
        }
        break;
    }
  };

  // Reset active index when dropdown opens/closes or filter changes
  useEffect(() => {
    if (expanded) {
      // Set active index to currently selected dashboard in filtered results
      const selectedIndex = filteredDashboards.findIndex(
        dashboard => dashboard.label === controller.selectedDashboard
      );
      setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    } else {
      setActiveIndex(-1);
      setSearchQuery(""); // Clear search when dropdown closes
    }
  }, [expanded, filteredDashboards, controller.selectedDashboard]);

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex >= 0 && expanded) {
      const activeElement = document.getElementById(`dashboard-option-${activeIndex}`);
      if (activeElement) {
        activeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }
  }, [activeIndex, expanded]);

  // Clear errors when dropdown is closed
  useEffect(() => {
    if (!expanded && controller.error) {
      controller.clearError();
    }
  }, [expanded, controller.error, controller.clearError]);

  // Track search usage
  useEffect(() => {
    if (searchQuery.trim() && expanded) {
      const timeoutId = setTimeout(() => {
        track('dashboard_search', {
          query: searchQuery,
          resultsCount: filteredDashboards.length,
          totalDashboards: dashboards.length,
        });
      }, 1000); // Debounce search tracking

      return () => clearTimeout(timeoutId);
    }
  }, [searchQuery, filteredDashboards.length, dashboards.length, expanded]);

  // Track dropdown open
  useEffect(() => {
    if (expanded) {
      track('dashboard_dropdown_opened', {
        dashboardCount: dashboards.length,
        selectedDashboard: controller.selectedDashboard,
      });
    }
  }, [expanded, dashboards.length, controller.selectedDashboard]);

  return (
    <>
      {confirmDelete()}
      <Popover
        isOpen={expanded}
        onClose={() => setExpanded(false)}
        closeOnBlur={true}
        placement="bottom-start"
        arrowSize={15}
      >
        <PopoverTrigger>
          <Flex>
            <DropDownButton
              aria-expanded={expanded}
              aria-haspopup="listbox"
              aria-controls="dashboard-listbox"
              aria-activedescendant={activeIndex >= 0 ? `dashboard-option-${activeIndex}` : undefined}
              onClick={() => setExpanded(!expanded)}
              onKeyDown={handleKeyDown}
            >
              <HStack cursor="pointer">
                {controller.selectedDashboard && (
                  <Text
                    maxW="200px"
                    minWidth="100px"
                    overflow={"hidden"}
                    textAlign={"left"}
                    textOverflow={"ellipsis"}
                    whiteSpace={"nowrap"}
                  >
                    {controller.selectedDashboard}
                  </Text>
                )}
                {!controller.selectedDashboard && (
                  <Text color="yellow.200">Not Specified</Text>
                )}
              </HStack>
            </DropDownButton>
          </Flex>
        </PopoverTrigger>
        <PopoverContent bg="var(--bg)" p={0} mt="-4px" minW="280px" maxW="92vw">
          <PopoverArrow bg="var(--bg)" />
          <PopoverBody py={2} m={0} w="full">
            <HStack justify="space-between" align="center">
            
              {controller.loading.fetching && <Spinner size="sm" />}
            </HStack>
            {controller.error && (
              <Text fontSize="xs" color="red.500" mb={2}>
                {controller.error}
              </Text>
            )}
            
            {/* Search input */}
            {dashboards.length > 3 && (
              <Input
                id="dashboard-search-input"
                placeholder="Search dashboards..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="sm"
                mb={2}
                fontSize="12px"
                onKeyDown={(e) => e.stopPropagation()}
                onKeyUp={(e) => e.stopPropagation()}
                onKeyPress={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              />
            )}
            
            <VStack align="stretch" p={0} spacing={0}>
              <Box 
                maxHeight="200px" 
                overflowY="auto"
                role="listbox"
                id="dashboard-listbox"
                aria-label="Dashboard selection"
                tabIndex={-1}
                onKeyDown={handleKeyDown}
              >
                {filteredDashboards.length > 0 ? (
                  filteredDashboards.map((dashboard, index) => (
                  <Button
                    key={index}
                    h="28px"
                    justifyContent="space-between"
                    bg={activeIndex === index ? "var(--accent-08)" : "transparent"}
                    borderColor={activeIndex === index ? "var(--accent)" : "transparent"}
                    borderWidth="1px"
                    onClick={(e) => {
                      e.preventDefault();
                      handleSelectDashboard(dashboard);
                    }}
                    width="full"
                    py={0}
                    pr={2}
                    pl={0}
                    isDisabled={controller.loading.creating || controller.loading.deleting}
                    role="option"
                    aria-selected={controller.selectedDashboard === dashboard.label}
                    id={`dashboard-option-${index}`}
                    tabIndex={-1}
                  >
                    <HStack w="full">
                      <Show if={editRow !== dashboard.value.configurationId}>
                        {controller.selectedDashboard !== dashboard.label ? (
                          <IoRadioButtonOff color="var(--white)" />
                        ) : (
                          <IoRadioButtonOn color="var(--white)" />
                        )}

                        <Tooltip label={dashboard.label} hasArrow placement="top">
                          <Text
                            maxW="200px"
                            overflow={"hidden"}
                            textOverflow={"ellipsis"}
                            fontSize="14px"
                            whiteSpace="nowrap"
                          >
                            {dashboard.label}
                          </Text>
                        </Tooltip>
                      </Show>
                      <Show if={editRow === dashboard.value.configurationId}>
                        <Input
                          h="28px"
                          w="full"
                          m="0"
                          onClick={(e) => e.stopPropagation()}
                          ref={renameDashboardInput}
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => {
                            // Prevent all keyboard events from bubbling up to prevent dropdown closure
                            e.stopPropagation();
                            
                            if (e.key === "Enter") {
                              e.preventDefault();
                              if (isRenameValid) {
                                track(EVENTS.DASHBOARD.DASHBOARD_RENAMED, {
                                  dashboardId: dashboard.value.configurationId,
                                  oldName: dashboard.label,
                                  newName: renameValue.trim(),
                                });
                                handleRenameDashboard(
                                  dashboard.value.configurationId!,
                                  renameValue.trim()
                                );
                              }
                            } else if (e.key === "Escape") {
                              e.preventDefault();
                              handleCancelEdit();
                            }
                          }}
                          onKeyUp={(e) => {
                            // Also prevent keyup events from bubbling
                            e.stopPropagation();
                          }}
                          onKeyPress={(e) => {
                            // Also prevent keypress events from bubbling
                            e.stopPropagation();
                          }}
                          onBlur={() => {
                            if (isRenameValid && renameValue.trim() !== dashboard.label) {
                              track(EVENTS.DASHBOARD.DASHBOARD_RENAMED, {
                                dashboardId: dashboard.value.configurationId,
                                oldName: dashboard.label,
                                newName: renameValue.trim(),
                              });
                              handleRenameDashboard(
                                dashboard.value.configurationId!,
                                renameValue.trim()
                              );
                            } else {
                              handleCancelEdit();
                            }
                          }}
                          isDisabled={controller.loading.renaming}
                          borderColor={!isRenameValid ? "red.500" : undefined}
                        />
                      </Show>
                      <Spacer />
                      <HStack spacing={1}>
                        <Tooltip label="Duplicate Dashboard">
                          <IoCopyOutline
                            fontSize="12px"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDuplicateDashboard(dashboard);
                            }}
                            color="var(--white)"
                            cursor="pointer"
                            opacity={controller.loading.creating || controller.loading.deleting ? 0.5 : 1}
                          />
                        </Tooltip>
                        <Tooltip label="Rename Dashboard">
                          <EditIcon
                            fontSize="12px"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartEdit(dashboard);
                            }}
                            color="var(--white)"
                            cursor="pointer"
                            opacity={controller.loading.creating || controller.loading.deleting ? 0.5 : 1}
                          />
                        </Tooltip>
                        <Tooltip
                          label={
                            dashboards.length === 1
                              ? "You can't delete your only dashboard."
                              : "Delete dashboard"
                          }
                        >
                          <DeleteIcon
                            fontSize="12px"
                            color={
                              dashboards.length === 1
                                ? "var(--dark-grey)"
                                : "var(--red)"
                            }
                            cursor={dashboards.length !== 1 ? "pointer" : "auto"}
                            opacity={(controller.loading.creating || controller.loading.deleting || dashboards.length === 1) ? 0.5 : 1}
                            onClick={(e) => {
                              if (dashboards.length === 1 || controller.loading.creating || controller.loading.deleting) {
                                return;
                              }
                              e.stopPropagation();
                              setDeleteDashboardRow(dashboard.value);
                              setShowAlert(true);
                            }}
                          />
                        </Tooltip>
                      </HStack>
                    </HStack>
                  </Button>
                ))) : (
                  <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                    {searchQuery ? `No dashboards found for "${searchQuery}"` : "No dashboards available"}
                  </Text>
                )}
              </Box>

              <Divider mt="2" />
              <Flex direction="column" py={2}>
                <Input
                  h="38px"
                  fontSize="14px"
                  ref={newDashboardInput}
                  placeholder="Add new dashboard"
                  value={newDashboardName}
                  onChange={(e) => setNewDashboardName(e.target.value)}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (isNewDashboardValid && !controller.loading.creating) {
                        track(EVENTS.DASHBOARD.DASHBOARD_CREATED, {
                          dashboardName: newDashboardName.trim(),
                        });
                        handleCreateDashboard();
                      }
                    }
                  }}
                  onKeyUp={(e) => e.stopPropagation()}
                  onKeyPress={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  isDisabled={controller.loading.creating}
                  borderColor={newDashboardName && !isNewDashboardValid ? "red.500" : undefined}
                />
                {newDashboardName && !isNewDashboardValid && (
                  <Text fontSize="xs" color="red.500" mt={1}>
                    {validateDashboardName(newDashboardName)}
                  </Text>
                )}
                <HStack mt={2} w="full">
                  <SecondaryButton
                    w="full"
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      if (isNewDashboardValid) {
                        track(EVENTS.DASHBOARD.DASHBOARD_CREATED, {
                          dashboardName: newDashboardName.trim(),
                        });
                        handleCreateDashboard();
                      }
                    }}
                    isDisabled={!isNewDashboardValid || controller.loading.creating}
                    isLoading={controller.loading.creating}
                  >
                    Create Dashboard
                  </SecondaryButton>
                </HStack>
              </Flex>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </>
  );
};
